import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import TopNav, { AppNavMode } from "../components/TopNav.tsx";
import GeneratingOverlay from "../components/GeneratingOverlay.tsx";
import { generatePracticeExam, PracticeExamQuestion, PracticeExamResponse } from "../services/questions.ts";
import { saveScore } from "../services/scores.ts";
import api from "../services/api.ts";
import SvgQuestionRenderer from "../components/SvgQuestionRenderer.tsx";
import { extractQuestionSvg } from "../utils/svgBundle.ts";

type SubjectOption = "Maths" | "English" | "Verbal Reasoning" | "Non-Verbal Reasoning" | "Science";

type ResultByQuestion = {
  is_correct: boolean;
  feedback: string;
};

const SUBJECT_OPTIONS: SubjectOption[] = [
  "Maths",
  "English",
  "Verbal Reasoning",
  "Non-Verbal Reasoning",
  "Science",
];

const normalizeText = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,!?;:]/g, "");

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const labelClass = "block text-xl font-semibold font-body text-gray-500 mb-1";
const fieldClass = "w-full rounded-lg border border-border px-3 py-2 bg-white text-ink text-xl font-body focus:outline-none focus:border-accent";

const PracticeExam: React.FC = () => {
  const QUESTIONS_PER_PAGE = 5;
  const navigate = useNavigate();

  const [subject, setSubject] = useState<SubjectOption>("Maths");
  const [topicsText, setTopicsText] = useState("Fractions\nDecimals\nRatios");
  const [country, setCountry] = useState("UK");
  const [age, setAge] = useState<number>(10);
  const [numQuestions, setNumQuestions] = useState<number>(10);
  const [durationMinutes, setDurationMinutes] = useState<number>(20);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [grading, setGrading] = useState(false);

  const [exam, setExam] = useState<PracticeExamResponse | null>(null);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const [mcqAnswers, setMcqAnswers] = useState<Record<string, string>>({});
  const [shortAnswers, setShortAnswers] = useState<Record<string, string>>({});

  const [results, setResults] = useState<Record<string, ResultByQuestion>>({});
  const [finalScore, setFinalScore] = useState({ correct: 0, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  const orderedQuestions = useMemo(() => exam?.questions || [], [exam]);
  const totalPages = Math.max(1, Math.ceil(orderedQuestions.length / QUESTIONS_PER_PAGE));
  const pageStart = (currentPage - 1) * QUESTIONS_PER_PAGE;
  const visibleQuestions = orderedQuestions.slice(pageStart, pageStart + QUESTIONS_PER_PAGE);

  const gradeShortAnswers = async (
    questions: PracticeExamQuestion[],
    answers: Record<string, string>
  ): Promise<Record<string, ResultByQuestion>> => {
    const token = localStorage.getItem("token");

    // Filter to only short answer questions
    const shortAnswerQs = questions.filter((q) => q.question_type !== "mcq");

    if (shortAnswerQs.length === 0) {
      return {};
    }

    try {
      const response = await api.post(
        "/questions/grade-short-answers",
        {
          questions: shortAnswerQs,
          answers: Object.fromEntries(
            Object.entries(answers).filter(([qid]) =>
              shortAnswerQs.some((q) => q.question_id === qid)
            )
          ),
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );

      const data = response.data;
      const byQuestion: Record<string, ResultByQuestion> = {};

      for (const result of data.results || []) {
        byQuestion[result.question_id] = {
          is_correct: result.is_correct,
          feedback: result.feedback,
        };
      }

      return byQuestion;
    } catch (err) {
      // Graceful fallback: mark all as incorrect
      console.error("Error grading short answers:", err);
      return Object.fromEntries(
        shortAnswerQs.map((q) => [
          q.question_id,
          {
            is_correct: false,
            feedback: "Could not grade answer automatically.",
          },
        ])
      );
    }
  };

  const generateExam = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSubmitted(false);
    setStarted(false);
    setExam(null);
    setResults({});
    setFinalScore({ correct: 0, total: 0 });
    setMcqAnswers({});
    setShortAnswers({});
    setCurrentPage(1);

    try {
      const response = await generatePracticeExam(
        subject,
        topicsText,
        numQuestions,
        durationMinutes,
        age,
        country
      );
      setExam(response.data);
      setSecondsLeft(response.data.duration_minutes * 60);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Unable to generate practice exam right now.");
    } finally {
      setLoading(false);
    }
  };

  const evaluateExam = async () => {
    if (!exam || submitted) {
      return;
    }

    let correct = 0;
    const byQuestion: Record<string, ResultByQuestion> = {};

    // Grade MCQ questions
    for (const question of exam.questions) {
      if (question.question_type === "mcq") {
        const selected = (mcqAnswers[question.question_id] || "").trim().toUpperCase();
        const expected = (question.correct_option || "").trim().toUpperCase();
        const isCorrect = selected.length > 0 && selected === expected;
        if (isCorrect) {
          correct += 1;
        }
        byQuestion[question.question_id] = {
          is_correct: isCorrect,
          feedback: isCorrect
            ? "Correct answer."
            : `Expected ${expected || "N/A"}. ${question.explanation}`,
        };
      }
    }

    // Short answers graded by AI — handled after the loop
    const shortAnswerQuestions = exam.questions.filter((q) => q.question_type !== "mcq");
    if (shortAnswerQuestions.length > 0) {
      setGrading(true);
      try {
        const aiResults = await gradeShortAnswers(shortAnswerQuestions, shortAnswers);
        for (const [qid, result] of Object.entries(aiResults)) {
          byQuestion[qid] = result;
          if (result.is_correct) correct += 1;
        }
      } finally {
        setGrading(false);
      }
    }

    setResults(byQuestion);
    setFinalScore({ correct, total: exam.questions.length });
    setSubmitted(true);
    setStarted(false);

    try {
      await saveScore({
        num_questions: exam.questions.length,
        num_correct: correct,
        subject: exam.subject,
        topic: exam.topics.join(", "),
      });
    } catch {
      // Non-blocking for UI result.
    }
  };

  useEffect(() => {
    if (!started || submitted) {
      return;
    }

    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(timer);
          void evaluateExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [started, submitted]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const canSubmit = started && !submitted;

  const renderQuestion = (question: PracticeExamQuestion, index: number) => {
    const questionResult = results[question.question_id];
    const cardBorder = !submitted
      ? "border-border"
      : questionResult?.is_correct
      ? "border-success"
      : "border-incorrect";
    const questionSvgCode = extractQuestionSvg(question.svg_code);

    return (
      <div key={question.question_id} className={`bg-white rounded-lg p-5 border ${submitted ? "border-2" : ""} ${cardBorder}`}>
        <div className="flex items-center justify-between mb-1">
          <span className="text-lg font-semibold font-body text-gray-500">Q{index + 1} of {orderedQuestions.length}</span>
          <span className="text-lg font-semibold font-body uppercase tracking-wide text-gray-500">{question.difficulty}</span>
        </div>
        <h3 className="text-3xl font-semibold font-display text-ink mb-3">{question.question_text}</h3>

        {questionSvgCode && (
          <SvgQuestionRenderer
            svgCode={questionSvgCode}
            questionText={question.question_text}
            className="mx-auto"
          />
        )}

        {question.question_type === "mcq" ? (
          <div className="grid gap-2">
            {(question.options || []).map((option, idx) => {
              const letter = String.fromCharCode(65 + idx);
              return (
                <label key={letter} className="flex items-center gap-3 rounded-lg border border-border bg-white px-3 py-2 cursor-pointer hover:bg-lightbg">
                  <input
                    type="radio"
                    name={question.question_id}
                    value={letter}
                    checked={mcqAnswers[question.question_id] === letter}
                    onChange={() => setMcqAnswers((prev) => ({ ...prev, [question.question_id]: letter }))}
                    disabled={!started || submitted}
                    className="accent-primary"
                  />
                  <span className="text-2xl font-body text-ink">{option}</span>
                </label>
              );
            })}
          </div>
        ) : (
          <textarea
            className={`${fieldClass} min-h-24`}
            value={shortAnswers[question.question_id] || ""}
            onChange={(e) =>
              setShortAnswers((prev) => ({
                ...prev,
                [question.question_id]: e.target.value,
              }))
            }
            placeholder="Type your answer here"
            disabled={!started || submitted}
          />
        )}

        {submitted && questionResult && (
          <div className={`mt-3 rounded-lg px-3 py-2 text-xl font-body ${questionResult.is_correct ? "bg-lightbg text-success" : "bg-lightbg text-incorrect"}`}>
            {questionResult.feedback}
          </div>
        )}
      </div>
    );
  };

  const handleModeNav = (target: AppNavMode) => {
    if (target === "practice-exam") {
      return;
    }
    navigate(`/questions?mode=${target}`);
  };

  const hasExamGenerated = exam !== null;

  return (
    <div className="min-h-screen">
      <TopNav activeMode="practice-exam" onSelect={handleModeNav} />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[300px_minmax(0,1fr)] gap-6 items-start">
          <main className="min-w-0 order-2 lg:order-2 relative">
            <GeneratingOverlay active={loading} title="AI is generating your practice exam..." numQuestions={numQuestions} />

            {!hasExamGenerated && (
              <div className="bg-white rounded-lg border border-border p-12 text-center mb-4">
                <h2 className="text-5xl font-bold font-display text-ink mb-3">Practice Exam Generator</h2>
                <p className="text-2xl font-body text-gray-600 mb-8">Take a full exam and assess your progress.</p>
                <div className="grid sm:grid-cols-3 gap-4 max-w-xl mx-auto text-left">
                  <div className="rounded-lg bg-lightbg p-4">
                    <div className="text-lg font-bold font-body text-primary mb-1">1. Set up</div>
                    <div className="text-lg font-body text-gray-600">Choose subject, topics and duration.</div>
                  </div>
                  <div className="rounded-lg bg-lightbg p-4">
                    <div className="text-lg font-bold font-body text-primary mb-1">2. Start</div>
                    <div className="text-lg font-body text-gray-600">Answer against the clock.</div>
                  </div>
                  <div className="rounded-lg bg-lightbg p-4">
                    <div className="text-lg font-bold font-body text-primary mb-1">3. Review</div>
                    <div className="text-lg font-body text-gray-600">Get instant marking and feedback.</div>
                  </div>
                </div>
              </div>
            )}

            {error && <div className="mb-4 rounded-lg bg-lightbg border border-incorrect text-incorrect px-4 py-3 font-body text-lg">{error}</div>}

            {exam && !started && !submitted && (
              <div className="mb-4 rounded-lg bg-white border border-border p-5">
                <h2 className="text-2xl font-bold font-display text-ink mb-2">Exam Ready</h2>
                <p className="text-xl font-body text-gray-600 mb-1">Subject: {exam.subject}</p>
                <p className="text-xl font-body text-gray-600 mb-1">Topics: {exam.topics.join(", ")}</p>
                <p className="text-xl font-body text-gray-600 mb-4">Questions: {exam.questions.length} | Duration: {exam.duration_minutes} minutes</p>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentPage(1);
                    setStarted(true);
                  }}
                  className="bg-primary text-white font-semibold font-body text-2xl py-2 px-6 rounded-lg hover:bg-ink transition-colors duration-200"
                >
                  Start Exam
                </button>
              </div>
            )}

            {exam && (
              <div className="mb-4 flex justify-between items-center">
                <div className="text-lg font-semibold font-body uppercase tracking-wide text-gray-500">{started ? "Exam in progress" : submitted ? "Exam submitted" : "Waiting to start"}</div>
                <div className={`text-3xl font-bold font-body ${secondsLeft <= 60 && started ? "text-incorrect" : "text-primary"}`}>{formatTime(secondsLeft)}</div>
              </div>
            )}

            {(started || submitted) && orderedQuestions.length > 0 && (
              <>
                <div className="mb-3 flex items-center justify-between text-xl font-semibold font-body text-gray-500">
                  <span>Page {currentPage} of {totalPages}</span>
                  <span>Showing questions {pageStart + 1}-{Math.min(pageStart + QUESTIONS_PER_PAGE, orderedQuestions.length)} of {orderedQuestions.length}</span>
                </div>

                <div className="grid gap-3 mb-4">
                  {visibleQuestions.map((question, idx) => renderQuestion(question, pageStart + idx))}
                </div>

                {orderedQuestions.length > QUESTIONS_PER_PAGE && (
                  <div className="mb-4 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="rounded-lg border border-primary px-5 py-2 font-semibold font-body text-primary disabled:cursor-not-allowed disabled:opacity-50 hover:bg-lightbg"
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="rounded-lg border border-primary px-5 py-2 font-semibold font-body text-primary disabled:cursor-not-allowed disabled:opacity-50 hover:bg-lightbg"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}

            {canSubmit && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => void evaluateExam()}
                  disabled={grading}
                  className="bg-primary text-white font-semibold font-body text-2xl py-3 px-8 rounded-lg hover:bg-ink transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Submit Exam
                </button>
                {grading && (
                  <span className="text-primary font-semibold font-body text-lg">Marking your answers...</span>
                )}
              </div>
            )}

            {submitted && (
              <div className="mt-6 rounded-lg bg-white border border-success p-5">
                <h2 className="text-3xl font-bold font-display text-ink mb-2">Result</h2>
                <p className="text-2xl font-body text-gray-600">
                  You scored {finalScore.correct} out of {finalScore.total}.
                </p>
                <p className="text-lg font-body text-gray-500 mt-2">Results are instant for both MCQ and short-answer questions using answer-key + keyword matching.</p>
              </div>
            )}
          </main>

          <aside className="order-1 lg:order-1 lg:sticky lg:top-20">
            <div className="bg-white rounded-lg border border-border p-5">
              <h3 className="text-2xl font-semibold font-display text-ink mb-3">Practice Exam Generator</h3>
              <form onSubmit={generateExam} className="flex flex-col gap-3">
                <div>
                  <label className={labelClass}>Subject</label>
                  <select
                    className={fieldClass}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value as SubjectOption)}
                  >
                    {SUBJECT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Country</label>
                  <select
                    className={fieldClass}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  >
                    <option value="UK">UK</option>
                    <option value="India">India</option>
                    <option value="US">US</option>
                    <option value="Sweden">Sweden</option>
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Age</label>
                  <select
                    className={fieldClass}
                    value={age}
                    onChange={(e) => setAge(Number(e.target.value))}
                  >
                    {Array.from({ length: 15 }, (_, i) => i + 6).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Number of Questions</label>
                  <select
                    className={fieldClass}
                    value={numQuestions}
                    onChange={(e) => setNumQuestions(Number(e.target.value))}
                  >
                    {Array.from({ length: 18 }, (_, i) => i + 3).map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Duration (minutes)</label>
                  <select
                    className={fieldClass}
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(Number(e.target.value))}
                  >
                    {[10, 15, 20, 25, 30, 45, 60].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Topics (one per line or comma-separated)</label>
                  <textarea
                    className={`${fieldClass} min-h-24`}
                    value={topicsText}
                    onChange={(e) => setTopicsText(e.target.value)}
                    placeholder="Fractions\nDecimals\nRatios"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white font-semibold font-body text-2xl py-2 px-4 rounded-lg hover:bg-ink transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Generating Exam..." : "Generate Exam"}
                </button>
              </form>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PracticeExam;
